import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { catchError, of, switchMap } from 'rxjs';

import { SidebarStateService } from '../shared/sidebar-state.service';

@Component({
  selector: 'app-sidebar',
  imports: [RouterLink, RouterLinkActive],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.css',
})
export class Sidebar implements OnInit, OnDestroy {
  private readonly sidebarState = inject(SidebarStateService);
  private readonly msalService = inject(MsalService);
  private readonly http = inject(HttpClient);
  private photoObjectUrl = '';

  protected readonly collapsed = this.sidebarState.collapsed;
  protected readonly userName = signal('Signed-in user');
  protected readonly userEmail = signal('');
  protected readonly userInitials = signal('U');
  protected readonly userPhotoUrl = signal('');
  ngOnInit(): void {
    const account = this.msalService.instance.getActiveAccount()
      ?? this.msalService.instance.getAllAccounts()[0];
    if (!account) {
      return;
    }

    const claims = account.idTokenClaims as Record<string, unknown> | undefined;
    const name = account.name
      || this.claimText(claims, 'name')
      || this.claimText(claims, 'given_name')
      || 'Signed-in user';
    const email = account.username
      || this.claimText(claims, 'preferred_username')
      || this.claimText(claims, 'email');

    this.userName.set(name);
    this.userEmail.set(email);
    this.userInitials.set(this.buildInitials(name, email));

    this.msalService.acquireTokenSilent({
      account,
      scopes: ['User.Read'],
    }).pipe(
      switchMap((result) => this.http.get('https://graph.microsoft.com/v1.0/me/photo/$value', {
        headers: new HttpHeaders({ Authorization: `Bearer ${result.accessToken}` }),
        responseType: 'blob',
      })),
      catchError(() => of(null)),
    ).subscribe((photo) => {
      if (!photo?.size) {
        return;
      }

      this.photoObjectUrl = URL.createObjectURL(photo);
      this.userPhotoUrl.set(this.photoObjectUrl);
    });
  }

  ngOnDestroy(): void {
    if (this.photoObjectUrl) {
      URL.revokeObjectURL(this.photoObjectUrl);
    }
  }

  protected onCollapse(): void {
    this.sidebarState.collapse();
  }

  protected onPhotoError(): void {
    if (this.photoObjectUrl) {
      URL.revokeObjectURL(this.photoObjectUrl);
      this.photoObjectUrl = '';
    }
    this.userPhotoUrl.set('');
  }

  private claimText(claims: Record<string, unknown> | undefined, key: string): string {
    const value = claims?.[key];
    return typeof value === 'string' ? value.trim() : '';
  }

  private buildInitials(name: string, email: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length > 0 && name !== 'Signed-in user') {
      return `${parts[0][0] ?? ''}${parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : ''}`.toUpperCase();
    }
    return (email[0] || 'U').toUpperCase();
  }
}
